import {useEffect} from 'react';

import {Box, Skeleton, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';

import {PaymentLinkLogo} from '@/app/main/paymentLink/components/PaymentLinkLogo/PaymentLinkLogo';
import {AppButton, AppButtonVariant} from '@/app/shared-components';
import './styles/ConfirmationPage.scss';

import SplitsConfirmationContentData from './components/SplitsConfirmationContentData';
import {SplitsConfirmationHeader} from './components/SplitsConfirmationHeader';
import SplitsConfirmationSkeleton from './components/SplitsConfirmationSkeleton';
import {SplitsPromoCard} from './components/SplitsPromoCard';
import {
  SplitsViewModelProvider,
  useSplitBillsViewModelProvider,
} from './SplitsViewModelContext';

function SplitsConfirmationContent() {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const {
    userName,
    inviterName,
    isLoadingSplitData,
    onGetAvatarImage,
    description,
    totalAmountPaid,
    participants,
    handlePay,
    inApp,
    handleSplitWithMo,
    handleOpenAccount,
  } = useSplitBillsViewModelProvider();

  const state = location.state as {
    otpVerified?: boolean;
  } | null;

  useEffect(() => {
    if (!state?.otpVerified) {
      const params = searchParams.toString();
      navigate(`/splits?${params}`, {replace: true});
    }
  }, [navigate, searchParams, state?.otpVerified]);

  if (!state?.otpVerified) {
    return null;
  }

  return (
    <section className="splits-confirmation">
      <Box className="splits-confirmation-container">
        {!inApp && (
          <Box className="splits-confirmation-logo">
            <PaymentLinkLogo />
          </Box>
        )}

        <SplitsConfirmationHeader
          userName={userName}
          onGetAvatarImage={onGetAvatarImage}
        />

        <Box className="splits-confirmation-card splits-confirmation-breakdown">
          <Typography
            className="splits-confirmation-card-title"
            component="h2">
            {t('split_bills_page.confirmation.breakdown_title')}
          </Typography>
          <Box className="splits-confirmation-breakdown-rows">
            {isLoadingSplitData ? (
              <SplitsConfirmationSkeleton />
            ) : (
              <SplitsConfirmationContentData
                inviterName={inviterName}
                totalAmountPaid={totalAmountPaid}
                description={description}
                participants={participants}
              />
            )}
          </Box>
          {isLoadingSplitData ? (
            <Skeleton
              variant="rounded"
              height={48}
              animation="wave"
              sx={{width: '100%', borderRadius: 2, mt: 2, mb: 1}}
            />
          ) : (
            <AppButton
              id="splits-confirmation-pay"
              onClick={handlePay}
              label={t('split_bills_page.confirmation.pay_button', {
                name: inviterName,
              })}
              variant={AppButtonVariant.onPrimary}
            />
          )}
          <Typography className="splits-confirmation-pay-hint">
            {inApp
              ? t('split_bills_page.confirmation.pay_hint_in_app')
              : t('split_bills_page.confirmation.pay_hint_no_app')}
          </Typography>
        </Box>

        <SplitsPromoCard
          inApp={inApp}
          handleSplitWithMo={handleSplitWithMo}
          handleOpenAccount={handleOpenAccount}
        />
      </Box>
    </section>
  );
}

const SplitsConfirmationPage = () => (
  <SplitsViewModelProvider>
    <SplitsConfirmationContent />
  </SplitsViewModelProvider>
);

export default SplitsConfirmationPage;
